import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { CalendarView, Booking } from '@/types';
import { calculateDateRange, isBookingInRange } from '@/utils/date-range-utils';
import { bookingsApi, Booking as ApiBooking } from '@/services/api';
import { useCompany } from '@/contexts/CompanyContext';

type DataMode = 'demo' | 'live';

interface UseDateRangeBookingsOptions {
  calendarView: CalendarView;
  selectedDate: Date;
  allBookings: Booking[];
  mode: DataMode;
}

interface UseDateRangeBookingsResult {
  bookings: Booking[];
  isLoading: boolean;
  error: string | null;
  dateRange: { startDate: Date; endDate: Date };
  refetchLiveBookings: () => void;
  removeBookingFromCache: (id: string) => void;
}

const safeJsonParse = (val: unknown): Record<string, unknown> | undefined => {
  if (!val) return undefined;
  if (typeof val === 'object') return val as Record<string, unknown>;
  try { return JSON.parse(val as string) as Record<string, unknown>; } catch { return undefined; }
};

export const mapApiBookingToBooking = (apiBooking: ApiBooking): Booking => ({
  id: String(apiBooking.id),
  resourceId: apiBooking.resourceId != null ? String(apiBooking.resourceId) : 'unallocated',
  resourceIds: apiBooking.resourceIds
    ? (safeJsonParse(apiBooking.resourceIds) as unknown as string[] | undefined)?.map?.(String)
    : undefined,
  projectId: apiBooking.projectId != null ? String(apiBooking.projectId) : null,
  projectName: apiBooking.projectName || '',
  costCentreId: apiBooking.costCentreId != null ? Number(apiBooking.costCentreId) : null,
  clientId: apiBooking.clientId != null ? Number(apiBooking.clientId) : null,
  description: apiBooking.description,
  startTime: apiBooking.startTime,
  endTime: apiBooking.endTime,
  timezone: apiBooking.timezone,
  color: apiBooking.color,
  durationType: apiBooking.durationType as Booking['durationType'],
  halfDayPeriod: apiBooking.halfDayPeriod as Booking['halfDayPeriod'],
  billingClassification: apiBooking.billingClassification as Booking['billingClassification'],
  workType: apiBooking.workType as Booking['workType'],
  taskDescription: apiBooking.taskDescription,
  bookingNotes: apiBooking.bookingNotes,
  status: apiBooking.status as Booking['status'],
  priority: apiBooking.priority as Booking['priority'],
  notifyReminders: apiBooking.notifyReminders,
  bookingTypeId: apiBooking.bookingTypeId != null ? String(apiBooking.bookingTypeId) : undefined,
  customer: apiBooking.customer,
  location: apiBooking.location,
  details: apiBooking.details,
  // Recurring booking fields
  recurrence: safeJsonParse(apiBooking.recurrence) as Booking['recurrence'],
  recurrenceGroupId: apiBooking.recurrenceGroupId || undefined,
  isRecurrenceParent: apiBooking.isRecurrenceParent || undefined,
  recurrencePending: apiBooking.recurrencePending || undefined,
  // Additional custom fields
  costCentreTask: apiBooking.costCentreTask,
  bookingReference: apiBooking.bookingReference,
  userChar: safeJsonParse(apiBooking.userChar) as Booking['userChar'],
  userNumber: safeJsonParse(apiBooking.userNumber) as Booking['userNumber'],
  userFlag: safeJsonParse(apiBooking.userFlag) as Booking['userFlag'],
  userDate: safeJsonParse(apiBooking.userDate) as Booking['userDate'],
  userNotes: safeJsonParse(apiBooking.userNotes) as Booking['userNotes'],
  userTime: safeJsonParse(apiBooking.userTime) as Booking['userTime'],
  requiredSkills: safeJsonParse(apiBooking.requiredSkills) as string[] | undefined,
  requiredRole: apiBooking.requiredRole || undefined,
  companyId: apiBooking.companyId || undefined,
  durationHours: apiBooking.durationHours,
  durationDays: apiBooking.durationDays,
});

export function useDateRangeBookings({
  calendarView,
  selectedDate,
  allBookings,
  mode,
}: UseDateRangeBookingsOptions): UseDateRangeBookingsResult {
  const [liveBookings, setLiveBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchIdRef = useRef(0);

  const dateRange = useMemo(
    () => calculateDateRange(calendarView, selectedDate),
    [calendarView, selectedDate]
  );

  // Get the selected company ID from the company dropdown so it can be
  // passed as a query parameter to the bookings date-range API.
  const { currentCompany, isLoading: isCompanyLoading } = useCompany();
  const companyIdRef = useRef(currentCompany?.id);
  companyIdRef.current = currentCompany?.id;

  const fetchLiveBookings = useCallback(async (start: Date, end: Date) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const currentFetchId = ++fetchIdRef.current;

    setIsLoading(true);
    setError(null);

    try {
      const startISO = start.toISOString();
      const endISO = end.toISOString();
      const apiBookings = await bookingsApi.getByDateRange(startISO, endISO, false, companyIdRef.current);

      if (abortControllerRef.current?.signal.aborted) return;

      const mapped = apiBookings.map(mapApiBookingToBooking);
      setLiveBookings(mapped);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const message = err instanceof Error ? err.message : 'Failed to fetch bookings for date range';
      console.error('Error fetching date-range bookings:', message);
      setError(message);
      setLiveBookings([]);
    } finally {
      // Only clear loading if this is still the latest fetch.
      // A superseded fetch's finally must not reset the flag while a
      // newer fetch is still in flight.
      if (fetchIdRef.current === currentFetchId) {
        setIsLoading(false);
      }
    }
  }, []);

  const allBookingsLength = allBookings.length;

  useEffect(() => {
    // Skip fetching until the company context has finished loading.
    // Without this guard the hook fires once with companyId=undefined
    // and again once the company resolves, doubling the API calls.
    if (isCompanyLoading || !currentCompany?.id) return;

    if (mode === 'live') {
      fetchLiveBookings(dateRange.startDate, dateRange.endDate);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currentCompany?.id and
  // isCompanyLoading trigger a re-fetch when the company finishes loading or changes.
  }, [mode, dateRange.startDate, dateRange.endDate, fetchLiveBookings, allBookingsLength, currentCompany?.id, isCompanyLoading]);

  const refetchLiveBookings = useCallback(() => {
    if (mode === 'live') {
      fetchLiveBookings(dateRange.startDate, dateRange.endDate);
    }
  }, [mode, dateRange.startDate, dateRange.endDate, fetchLiveBookings]);

  const demoBookings = useMemo(() => {
    if (mode !== 'demo') return [];

    return allBookings.filter((booking) =>
      isBookingInRange(
        booking.startTime,
        booking.endTime,
        dateRange.startDate,
        dateRange.endDate
      )
    );
  }, [mode, allBookings, dateRange.startDate, dateRange.endDate]);

  // Remove a booking from the hook's local cache so it disappears from
  // the timeline immediately without waiting for an API refetch.
  const removeBookingFromCache = useCallback((id: string) => {
    setLiveBookings(prev => prev.filter(b => String(b.id) !== String(id)));
  }, []);

  // For live mode, merge API-fetched bookings with any newly created bookings
  // from the context that haven't been fetched yet. This ensures bookings
  // appear immediately on the timeline after creation without waiting for
  // the next date-range API call.
  // Context bookings carry optimistic updates (e.g. after drag-drop or edit)
  // so we overlay them onto API-fetched data to keep utilisation and display
  // in sync before the next refetch completes.
  const mergedLiveBookings = useMemo(() => {
    if (mode !== 'live') return [];

    // Build a lookup of context bookings so we can merge optimistic updates
    const contextMap = new Map<string, Booking>();
    for (const b of allBookings) {
      contextMap.set(String(b.id), b);
    }

    const fetchedIds = new Set(liveBookings.map(b => String(b.id)));

    // For API-fetched bookings, overlay the context version on top so that
    // optimistic updates (changed times, resource, status, etc.) are
    // reflected immediately. API fields act as defaults for anything the
    // context doesn't carry.
    const mergedFetched = liveBookings.map(b => {
        const ctx = contextMap.get(String(b.id));
        if (ctx) {
          return { ...b, ...ctx };
        }
        return b;
      });

    const newContextBookings = allBookings.filter((booking) => {
      const id = String(booking.id);
      if (fetchedIds.has(id)) return false;
      return isBookingInRange(
        booking.startTime,
        booking.endTime,
        dateRange.startDate,
        dateRange.endDate
      );
    });

    return [...mergedFetched, ...newContextBookings];
  }, [mode, liveBookings, allBookings, dateRange.startDate, dateRange.endDate]);

  const bookings = mode === 'demo' ? demoBookings : mergedLiveBookings;

  return {
    bookings,
    isLoading,
    error,
    dateRange,
    refetchLiveBookings,
    removeBookingFromCache,
  };
}
